import {
    definitions,
    fetchGetLoanDocumentsRequiredCreditFacility
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { deserialiseToHTML, getCookie } from '../../../../../api/HelperAPI';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';

export type LoanDocumentType = definitions['loan_document']['type'];

export function useGetDocument(documentType: LoanDocumentType) {
    const stateKey = useStateKey();
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const offerId = searchParams.get('offerId') ?? '';
    const amount = searchParams.get('amount') ?? '';

    const {
        data: document,
        isLoading: isDocumentListLoading,
        isError: isDocumentListError
    } = useQuery({
        queryKey: [
            'fetchGetLoanDocumentsRequiredCreditFacility',
            stateKey,
            offerId,
            amount
        ],
        queryFn: () =>
            fetchGetLoanDocumentsRequiredCreditFacility({
                stateKey,
                offerId,
                query: { cash_amount: Number(amount) }
            }),
        select: (response) => {
            const document = response.data.required_loan_documents.find(
                (doc) => doc.type === documentType
            );
            if (!document) {
                throw new Error(`No document found for type: ${documentType}`);
            }
            return document;
        }
    });

    const documentId = document?.document_id;

    const {
        data: documentHtml,
        isLoading: isDocumentHtmlLoading,
        isError: isDocumentHtmlError
    } = useQuery({
        queryKey: ['fetchGetLoanDocumentsUnsigned', stateKey, documentId],
        queryFn: async () => {
            const url = `/api/lending/edge/loan_documents/${stateKey}/unsigned/${documentId}/`;

            // using fetch because the lapi-client fetchGetLoanDocumentsUnsigned doesn't send Accept: text/html, causing a 406
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(url, {
                credentials: 'same-origin',
                method: 'GET',
                headers: {
                    'X-CSRFToken': getCookie('csrftoken') ?? '',
                    Accept: 'text/html'
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }
            const [htmlData] = await deserialiseToHTML(response);
            return htmlData.data;
        },
        enabled: Boolean(documentId)
    });

    const downloadUrl = `/api/lending/edge/loan_documents/${stateKey}/unsigned/${documentId}/?file_format=pdf`;

    return {
        documentId,
        documentHtml,
        downloadUrl,
        hasSigned: document?.agreed ?? false,
        isLoading: isDocumentListLoading || isDocumentHtmlLoading,
        isError: isDocumentListError || isDocumentHtmlError
    };
}
